// Pixel Office Constants

// Grid - expanded office layout (28x28)
export const TILE_SIZE = 16;
export const DEFAULT_COLS = 28; // Expanded layout for 12+ agents
export const DEFAULT_ROWS = 28; // Expanded layout with CEO office, conference, break room

// Character Animation
export const WALK_SPEED_PX_PER_SEC = 48;
export const WALK_FRAME_DURATION_SEC = 0.15;
export const TYPE_FRAME_DURATION_SEC = 0.3;
export const WANDER_PAUSE_MIN_SEC = 2.0;
export const WANDER_PAUSE_MAX_SEC = 20.0;

// Matrix Effect (spawn/despawn)
export const MATRIX_EFFECT_DURATION_SEC = 0.3;
export const MATRIX_TRAIL_LENGTH = 6;
export const MATRIX_SPRITE_COLS = 16;
export const MATRIX_SPRITE_ROWS = 24;
export const MATRIX_FLICKER_FPS = 30;
export const MATRIX_FLICKER_VISIBILITY_THRESHOLD = 180;
export const MATRIX_COLUMN_STAGGER_RANGE = 0.3;
export const MATRIX_HEAD_COLOR = "#ccffcc";
export const MATRIX_TRAIL_OVERLAY_ALPHA = 0.6;
export const MATRIX_TRAIL_EMPTY_ALPHA = 0.5;
export const MATRIX_TRAIL_MID_THRESHOLD = 0.33;
export const MATRIX_TRAIL_DIM_THRESHOLD = 0.66;

// Rendering
export const CHARACTER_SITTING_OFFSET_PX = 4; // Slight offset when typing at desk
export const CHARACTER_COUCH_SITTING_OFFSET_PX = 10; // Larger offset for couch sitting (sinks into cushion)
export const CHARACTER_Z_SORT_OFFSET = 0.5;
export const OUTLINE_Z_SORT_OFFSET = 0.001;
export const SELECTED_OUTLINE_ALPHA = 1.0;
export const HOVERED_OUTLINE_ALPHA = 0.5;

// Bubbles
export const BUBBLE_FADE_DURATION_SEC = 0.5;
export const BUBBLE_SITTING_OFFSET_PX = 10;
export const BUBBLE_VERTICAL_OFFSET_PX = 24;
export const WAITING_BUBBLE_DURATION_SEC = 2.0;
export const MESSAGE_BUBBLE_DURATION_SEC = 3.0; // Duration to show message notification
export const DISMISS_BUBBLE_FAST_FADE_SEC = 0.3;

// Camera
export const ZOOM_MIN = 1;
export const ZOOM_MAX = 6;
export const ZOOM_DEFAULT = 2; // Zoomed out to see full 28x28 office layout

// Idle Behavior - characters wander when not working
// Make characters chill - they should sit/stand for realistic durations
export const IDLE_BEFORE_WANDER_SEC = 30.0; // Wait at desk before wandering (longer = less wandering)
export const IDLE_COUCH_MIN_SEC = 30.0; // Sit on couch at least 30s
export const IDLE_COUCH_MAX_SEC = 90.0; // Sit on couch up to 90s
export const IDLE_COFFEE_MIN_SEC = 15.0; // Get coffee/water 15s min
export const IDLE_COFFEE_MAX_SEC = 45.0; // Get coffee/water 45s max
export const IDLE_STAND_MIN_SEC = 10.0; // Stand by something 10s min
export const IDLE_STAND_MAX_SEC = 30.0; // Stand by something 30s max

// Team Collaboration - multi-agent meeting behavior
export const MEETING_CHANCE_PER_IDLE = 0.15; // 15% chance to start meeting when picking idle behavior
export const MEETING_MIN_AGENTS = 2; // Minimum agents needed for a meeting
export const MEETING_DURATION_MIN_SEC = 20.0; // Minimum meeting duration
export const MEETING_DURATION_MAX_SEC = 60.0; // Maximum meeting duration
export const COLLABORATION_DURATION_MIN_SEC = 15.0; // Minimum pair collaboration duration
export const COLLABORATION_DURATION_MAX_SEC = 45.0; // Maximum pair collaboration duration
export const HANDOFF_ANIMATION_DURATION_SEC = 1.5; // Duration of handoff animation

// User Avatar (CEO) configuration
export const USER_AVATAR_PALETTE = 13; // Special palette index for user avatar (golden executive)
export const USER_AVATAR_DESK_COL = 22; // CEO desk X position (corner office - cols 18-26)
export const USER_AVATAR_DESK_ROW = 3; // CEO desk Y position (behind desk at row 4, facing down)

// CEO Office Zone - restricted area that only the user avatar (CEO) can enter
// Band members (workers) should never wander into this zone
export const CEO_OFFICE_ZONE = {
  minCol: 18,
  maxCol: 26,
  minRow: 1,
  maxRow: 7,
};

/**
 * Check if a position is inside the CEO office zone
 */
export function isInCeoOfficeZone(col: number, row: number): boolean {
  return (
    col >= CEO_OFFICE_ZONE.minCol &&
    col <= CEO_OFFICE_ZONE.maxCol &&
    row >= CEO_OFFICE_ZONE.minRow &&
    row <= CEO_OFFICE_ZONE.maxRow
  );
}

// Main agent (Ajax) desk position - sits in CEO office (behind desk, facing down)
export const MAIN_AGENT_DESK_COL = 22;
export const MAIN_AGENT_DESK_ROW = 3;

// Discussion animation - alternating speech bubbles
export const DISCUSSION_BUBBLE_INTERVAL_SEC = 2.0; // Time between speaker changes
export const DISCUSSION_BUBBLE_DURATION_SEC = 1.5; // How long each speaker's bubble shows

// Working together animation
export const WORKING_TOGETHER_PULSE_SPEED = 2.0; // Speed of connection pulse animation
export const WORKING_TOGETHER_LINE_COLOR = "rgba(100, 255, 150, 0.4)"; // Green working together line

// Idle behavior points in the office (tile coordinates matching 28x28 layout)
// Based on actual walkable tiles and furniture positions
export interface IdlePoint {
  col: number;
  row: number;
  activity: "couch" | "coffee" | "water" | "stand" | "window" | "meeting" | "sleep";
  facing: number; // Direction to face
  /** Meeting seat index (0-9) for meeting room chairs */
  meetingSeatIndex?: number;
}

export const IDLE_POINTS: Record<string, IdlePoint> = {
  // === CONFERENCE ROOM (cols 2-12, rows 2-6) ===
  // Large table at center, 10 seats around it
  // Left side of table - facing RIGHT (2)
  meetingSeat0: { col: 3, row: 2, activity: "meeting", facing: 2, meetingSeatIndex: 0 },
  meetingSeat1: { col: 3, row: 3, activity: "meeting", facing: 2, meetingSeatIndex: 1 },
  meetingSeat2: { col: 3, row: 4, activity: "meeting", facing: 2, meetingSeatIndex: 2 },
  meetingSeat3: { col: 3, row: 5, activity: "meeting", facing: 2, meetingSeatIndex: 3 },
  // Right side of table - facing LEFT (1)
  meetingSeat4: { col: 10, row: 2, activity: "meeting", facing: 1, meetingSeatIndex: 4 },
  meetingSeat5: { col: 10, row: 3, activity: "meeting", facing: 1, meetingSeatIndex: 5 },
  meetingSeat6: { col: 10, row: 4, activity: "meeting", facing: 1, meetingSeatIndex: 6 },
  meetingSeat7: { col: 10, row: 5, activity: "meeting", facing: 1, meetingSeatIndex: 7 },
  // Head/foot of table
  meetingSeat8: { col: 6, row: 2, activity: "meeting", facing: 0, meetingSeatIndex: 8 }, // Head - DOWN
  meetingSeat9: { col: 6, row: 6, activity: "meeting", facing: 3, meetingSeatIndex: 9 }, // Foot - UP

  // === CEO OFFICE (cols 18-26, rows 1-7) - Corner with windows ===
  ceoDesk: { col: 22, row: 4, activity: "stand", facing: 0 }, // CEO looking down at team
  ceoWindow1: { col: 25, row: 2, activity: "window", facing: 2 }, // CEO by right window
  ceoWindow2: { col: 22, row: 1, activity: "window", facing: 3 }, // CEO by top window

  // === MAIN WORK AREA (cols 1-26, rows 9-19) ===
  // Standing spots near desks for casual chats
  nearDesk1: { col: 5, row: 12, activity: "stand", facing: 0 },
  nearDesk2: { col: 11, row: 12, activity: "stand", facing: 0 },
  nearDesk3: { col: 17, row: 12, activity: "stand", facing: 0 },
  nearDesk4: { col: 23, row: 12, activity: "stand", facing: 0 },
  // Hallway/aisle standing
  workAisle1: { col: 12, row: 15, activity: "stand", facing: 0 },
  workAisle2: { col: 12, row: 18, activity: "stand", facing: 0 },

  // === BREAK ROOM (cols 14-26, rows 21-26) ===
  // Couches - large cushioned chairs for relaxing
  couch1: { col: 16, row: 22, activity: "couch", facing: 2 }, // RIGHT
  couch2: { col: 22, row: 22, activity: "couch", facing: 1 }, // LEFT
  couch3: { col: 16, row: 25, activity: "couch", facing: 2 }, // RIGHT
  couch4: { col: 22, row: 25, activity: "couch", facing: 1 }, // LEFT
  // Coffee table area
  coffeeTable: { col: 19, row: 23, activity: "stand", facing: 0 },

  // === KITCHEN (cols 1-13, rows 21-26) ===
  // Water cooler
  waterCooler: { col: 4, row: 23, activity: "water", facing: 3 }, // UP
  // Vending machine
  vendingMachine: { col: 8, row: 23, activity: "coffee", facing: 3 }, // UP
  // Coffee machine
  coffeeMachine: { col: 11, row: 23, activity: "coffee", facing: 3 }, // UP
  // Kitchen counter standing
  kitchenCounter: { col: 6, row: 25, activity: "stand", facing: 0 },

  // === SLEEP/REST SPOTS (scattered in break room) ===
  // Break room rest spots - agents sleep here when idle
  bunk1: { col: 15, row: 22, activity: "sleep", facing: 0 },
  bunk2: { col: 18, row: 22, activity: "sleep", facing: 0 },
  bunk3: { col: 21, row: 22, activity: "sleep", facing: 0 },
  bunk4: { col: 24, row: 22, activity: "sleep", facing: 0 },
  bunk5: { col: 15, row: 24, activity: "sleep", facing: 0 },
  bunk6: { col: 18, row: 24, activity: "sleep", facing: 0 },
  bunk7: { col: 21, row: 24, activity: "sleep", facing: 0 },
  bunk8: { col: 24, row: 24, activity: "sleep", facing: 0 },
  // Kitchen rest spots
  bunk9: { col: 3, row: 24, activity: "sleep", facing: 0 },
  bunk10: { col: 6, row: 24, activity: "sleep", facing: 0 },
  bunk11: { col: 9, row: 24, activity: "sleep", facing: 0 },
  bunk12: { col: 12, row: 24, activity: "sleep", facing: 0 },
};

// Get all meeting room idle points
export function getMeetingPoints(): IdlePoint[] {
  return Object.values(IDLE_POINTS).filter((p) => p.activity === "meeting");
}

// Get meeting point by seat index
export function getMeetingPointBySeat(seatIndex: number): IdlePoint | null {
  const points = Object.values(IDLE_POINTS);
  return points.find((p) => p.activity === "meeting" && p.meetingSeatIndex === seatIndex) ?? null;
}

// Colors - warm cozy office palette (Stardew Valley inspired)
export const FLOOR_COLOR = "#4a4458"; // Warm purple-ish wood
export const FLOOR_ACCENT = "#423d50"; // Darker plank accent
export const FLOOR_HIGHLIGHT = "#534e62"; // Lighter plank highlight
export const DESK_COLOR = "#8B6914";
export const WALL_COLOR = "#3d3850"; // Warm purple wall
export const WALL_TRIM = "#4a4560"; // Wall trim/baseboard
export const LOUNGE_FLOOR = "#4d4862"; // Carpet in lounge
export const KITCHEN_FLOOR = "#3d4848"; // Tile in kitchen
export const SHADOW_COLOR = "rgba(0,0,0,0.15)"; // Soft shadows

// Interactive office supply positions (for missing integrations)
// These are placed near desks/workspaces where agents can interact with them
import type { IntegrationType } from "./types/index.js";

export interface InteractiveSupplyConfig {
  type: IntegrationType;
  /** Grid column position */
  col: number;
  /** Grid row position */
  row: number;
  /** Width in pixels */
  width: number;
  /** Height in pixels */
  height: number;
  /** Tooltip text */
  tooltip: string;
}

// Office supply positions - placed in logical office sections
// Positioned near work desks (rows 10-11, 13-14, 16-17)
export const INTERACTIVE_SUPPLIES: InteractiveSupplyConfig[] = [
  // Phone on communication desk (for iMessage/communication setup)
  { type: "phone", col: 3, row: 17, width: 16, height: 16, tooltip: "Set up Phone/iMessage" },
  // Computer monitor near research desk (for kernel.sh/browser setup)
  { type: "computer", col: 9, row: 11, width: 16, height: 16, tooltip: "Set up Browser/Research" },
  // Notepad on PM desk (for notes integration)
  { type: "notepad", col: 9, row: 14, width: 16, height: 16, tooltip: "Set up Notes" },
  // Calendar on wall near conference room entrance (for calendar integration)
  { type: "calendar", col: 7, row: 8, width: 16, height: 16, tooltip: "Set up Calendar" },
  // Mail tray on support desk (for email setup)
  { type: "email", col: 9, row: 17, width: 16, height: 16, tooltip: "Set up Email" },
];

// Interactive object glow animation - subtle indicator
export const INTERACTIVE_GLOW_COLOR = "rgba(255, 208, 0, 0.5)"; // Subtle Rax yellow
export const INTERACTIVE_GLOW_PULSE_SPEED = 2.0; // Gentle pulse
export const INTERACTIVE_GLOW_MIN_ALPHA = 0.15; // Low minimum for subtlety
export const INTERACTIVE_GLOW_MAX_ALPHA = 0.4; // Moderate peak glow
export const INTERACTIVE_HOVER_SCALE = 1.15; // Slight hover scale

// Task types that agents can perform
export type TaskType = "research" | "communication" | "notes" | "calendar";

// Task desk positions - where agents walk to for specific task types
export interface TaskDesk {
  col: number;
  row: number;
  facing: number; // Direction to face when working
}

export const TASK_DESKS: Record<TaskType, TaskDesk> = {
  // Research/lookup tasks - walk to research desk area (row 11)
  research: { col: 9, row: 11, facing: 0 },
  // Communication tasks (email, messaging) - walk to communication desk
  communication: { col: 3, row: 17, facing: 0 },
  // Note-taking tasks - walk to PM desk area
  notes: { col: 9, row: 14, facing: 0 },
  // Calendar/scheduling tasks - walk to analyst desk area
  calendar: { col: 15, row: 11, facing: 0 },
};

// Keywords that map to task types for detection
export const TASK_KEYWORDS: Record<TaskType, string[]> = {
  research: ["research", "look up", "find", "search", "investigate", "explore", "discover"],
  communication: ["send email", "email", "draft", "message", "reply", "respond", "write to"],
  notes: ["take notes", "remember", "write down", "note", "jot down", "record", "save"],
  calendar: ["schedule", "calendar", "meeting", "appointment", "book", "plan"],
};

// Tool names that map to task types - for real-time activity tracking
// When agent uses these tools, they walk to the appropriate desk
export const TOOL_TO_TASK: Record<string, TaskType> = {
  // Research tools - agent goes to research desk
  Read: "research",
  Grep: "research",
  Glob: "research",
  WebFetch: "research",
  WebSearch: "research",
  Task: "research", // Subagent exploration
  // Communication tools - agent goes to communication desk
  SendMessage: "communication",
  Email: "communication",
  Slack: "communication",
  Discord: "communication",
  Telegram: "communication",
  iMessage: "communication",
  WhatsApp: "communication",
  // Notes/writing tools - agent goes to notes desk
  Write: "notes",
  Edit: "notes",
  NotebookEdit: "notes",
  // Calendar/scheduling tools - agent goes to calendar desk
  Calendar: "calendar",
  Schedule: "calendar",
  CreateEvent: "calendar",
};

/**
 * Detect task type from tool name
 * Returns the task type if the tool maps to one, or null if no match
 */
export function detectTaskFromTool(toolName: string): TaskType | null {
  return TOOL_TO_TASK[toolName] ?? null;
}

/**
 * Detect task type from user message content
 * Returns the task type if detected, or null if no match
 */
export function detectTaskType(message: string): TaskType | null {
  const lowerMessage = message.toLowerCase();

  for (const [taskType, keywords] of Object.entries(TASK_KEYWORDS) as Array<[TaskType, string[]]>) {
    for (const keyword of keywords) {
      if (lowerMessage.includes(keyword)) {
        return taskType;
      }
    }
  }

  return null;
}

// ============================================================================
// Rax Band Members - PE Portfolio Edition
// ============================================================================

/** Band member configuration for pixel office visualization */
export interface BandMemberConfig {
  id: string;
  name: string;
  icon: string;
  color: string;
  palette: number; // Character sprite palette index
  /** Home desk position - where agent works when active */
  homeDesk: { col: number; row: number };
}

/**
 * 12 band members for the pixel office (supporting cast)
 * NOTE: The user's main agent (e.g., Ajax) IS the Orchestrator - not listed here
 * Each has a unique palette (0-11 for 12 unique sprites) and home desk position
 *
 * Layout: 3 rows of 4 desks each in the main work area
 * Characters sit AT desk row (behind desk furniture which is one row south)
 */
export const BAND_MEMBERS: BandMemberConfig[] = [
  // Row 1 (row 10): Research & Analysis team - desks at row 11
  { id: "researcher", name: "Researcher", icon: "🔍", color: "#4A90D9", palette: 0, homeDesk: { col: 3, row: 10 } },
  { id: "analyst", name: "Analyst", icon: "📊", color: "#9B59B6", palette: 1, homeDesk: { col: 9, row: 10 } },
  { id: "bi", name: "BI", icon: "🎯", color: "#8E44AD", palette: 2, homeDesk: { col: 15, row: 10 } },
  { id: "coder", name: "Coder", icon: "💻", color: "#50C878", palette: 3, homeDesk: { col: 21, row: 10 } },
  // Row 2 (row 13): Engineering & Ops team - desks at row 14
  { id: "security", name: "Security", icon: "🔒", color: "#2C3E50", palette: 4, homeDesk: { col: 3, row: 13 } },
  { id: "pm", name: "PM", icon: "📋", color: "#3498DB", palette: 5, homeDesk: { col: 9, row: 13 } },
  { id: "legal", name: "Legal", icon: "⚖️", color: "#34495E", palette: 6, homeDesk: { col: 15, row: 13 } },
  { id: "people", name: "People", icon: "👥", color: "#E91E63", palette: 7, homeDesk: { col: 21, row: 13 } },
  // Row 3 (row 16): Business & Communication team - desks at row 17
  { id: "communicator", name: "Communicator", icon: "✍️", color: "#E74C3C", palette: 8, homeDesk: { col: 3, row: 16 } },
  { id: "support", name: "Support", icon: "🤝", color: "#1ABC9C", palette: 9, homeDesk: { col: 9, row: 16 } },
  { id: "sales", name: "Sales", icon: "💰", color: "#27AE60", palette: 10, homeDesk: { col: 15, row: 16 } },
  { id: "finance", name: "Finance", icon: "📈", color: "#F39C12", palette: 11, homeDesk: { col: 21, row: 16 } },
];

/**
 * Get a band member config by ID
 */
export function getBandMember(id: string): BandMemberConfig | undefined {
  return BAND_MEMBERS.find((m) => m.id === id);
}

/**
 * Get all sleep points for agents to rest at
 */
export function getSleepPoints(): IdlePoint[] {
  return Object.values(IDLE_POINTS).filter((p) => p.activity === "sleep");
}
